import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type SetStateAction } from 'react'
import * as cartService from '../core/cartService'
import { type CartItem, type PartialCartItem } from '../types'
import { validateCartItem } from '../utils/validateCartItem'
import { defaultCartValues, useFpCampaignCartSelector, useFpCampaignCartStore } from './useFpCampaignCartStore'

export const useFpCampaignCartService = (campaignId: number | null) => {
  const cart = useFpCampaignCartSelector(campaignId, state => state.cart)
  const [, setCartValues] = useFpCampaignCartStore(campaignId)

  const deleteCartItem = useCallbackEvent((item: PartialCartItem) => setCartValues((state) => {
    if (!validateCartItem(item)) return state
    return {
      ...state,
      cart: cartService.deleteCartItem(state.cart, item),
    }
  }))

  const updateCartItemButlerIsDeleted = useCallbackEvent((item: PartialCartItem, isDeleted: boolean) => {
    setCartValues((state) => {
      if (!validateCartItem(item)) return state
      return {
        ...state,
        cart: cartService.updateCartItemButlerIsDeleted(state.cart, item, isDeleted),
      }
    })
  })

  const updateCartItemButlerChecked = useCallbackEvent((item: PartialCartItem, checked: boolean) => {
    setCartValues((state) => {
      if (!validateCartItem(item)) return state
      return {
        ...state,
        cart: cartService.updateCartItemButlerChecked(state.cart, item, checked),
      }
    })
  })

  const updateAllCartItemsButlerChecked = useCallbackEvent((checked: boolean) => {
    setCartValues((state) => {
      return {
        ...state,
        cart: cartService.updateAllCartItemsButlerChecked(state.cart, checked),
      }
    })
  })

  const upsertCartItem = useCallbackEvent((item: PartialCartItem) => {
    setCartValues((state) => {
      if (!validateCartItem(item)) return state
      return {
        ...state,
        cart: cartService.upsertCartItem(state.cart, item),
      }
    })
  })

  const replaceCartItem = useCallbackEvent((targetCartItemId: CartItem['cartItemId'], item: PartialCartItem) => {
    setCartValues((state) => {
      if (!validateCartItem(item)) return state
      return {
        ...state,
        cart: cartService.replaceCartItem(state.cart, targetCartItemId, item),
      }
    })
  })

  const updateCartItems = useCallbackEvent((values: SetStateAction<CartItem[]>) => {
    setCartValues((state) => {
      const items = values instanceof Function ? values(state.cart.items) : values
      return {
        ...state,
        cart: cartService.updateCartItems(state.cart, items),
      }
    })
  })

  const resetCartValues = useCallbackEvent(() => {
    setCartValues(defaultCartValues)
  })

  const isCartEmpty = cartService.getIsCartEmpty(cart)

  /**
   * SPEC:
   * 情境1  車內品項不可同時結帳
   *   公益商品只能單獨結帳 (公益商品運送類別為 donation)
   *   常溫冷凍不能同時結帳
   */
  const isCartItemAddForbidden = useCallbackEvent(({
    itemWantToAdd,
  }: {
    itemWantToAdd: PartialCartItem
    currentQuantity?: number
  }) => {
    // 公益商品、常溫冷凍不能同時結帳
    const mixedDeliveryClassIsForbidden = !!itemWantToAdd.delivery_class && cart.items.some(item => item.delivery_class !== itemWantToAdd.delivery_class)
    const isForbidden = mixedDeliveryClassIsForbidden
    if (isForbidden) {
      return {
        mixedDeliveryClassIsForbidden,
      }
    }
    return false
  })

  return {
    ...cart,
    isCartEmpty,
    resetCartValues,
    deleteCartItem,
    upsertCartItem,
    replaceCartItem,
    updateCartItems,
    updateCartItemButlerIsDeleted,
    updateCartItemButlerChecked,
    updateAllCartItemsButlerChecked,
    isCartItemAddForbidden,
  }
}
